import { Box, Grid, useTheme } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'
// your existing rich text editor
import CommentList from './CommentList'
import PlaceholderComment from './PlaceholderComment'
import TaskSelector from './TaskSelector'

import { useDeleteAttachment } from 'api/aws/comments/useDeleteAttachment'
import { useDeleteCommentAndAttachments } from 'api/aws/comments/useDeleteCommentAndAttachments'
import { useHandlePostComment } from 'api/aws/comments/useHandlePostComment'
import { useUniversalNotify } from 'api/aws/notifications/useUniversalNotify'
import { useSyncQueryCache } from 'api/aws/organizations_table/useSyncQueryCache'
import { useUploadNoteAttachments } from 'api/aws/s3/useUploadNoteAttachments'
import { useUserItems } from 'api/aws/users/useUserItems'
import { useUsersById } from 'api/customHooks/useUsersById'
import { useEntitiesFromSearch } from 'api/search'
import { useActiveUser } from 'api/slack/useActiveUser'
import { useSlackNotification } from 'api/slack/useSlackNotification'
import TextEditor from 'components/TextEditor/TextEditor'
import { useEditorContext } from 'context/EditorContext/EditorContext'
import { useSnackbarContext } from 'context/SnackbarProvider/SnackbarProvider'
import { Attachments, Organization, OrganizationTableItem, SelectedTask, User } from 'types'

// ---- Types from your app (adjust if needed) ----
interface _UploadedAttachment {
  name: string
  url: string
  mimeType: string
  thumbnailLink?: string
  iconLink?: string
}
interface CommentsInputProps {
  contextFilters?: string[] | null
  expanded: boolean
  checklistItems: object[] | []
  context: 'organization' | 'deals' | 'accounting' | 'nco' | 'creative' | 'website' | string
  taskSKs: string[]
  onAddTaskComment?: (selectedItem: any, newNoteObj: any) => Promise<void>
}

const byNewest = (a: any, b: any) => new Date(b?.createdAt || 0).getTime() - new Date(a?.createdAt || 0).getTime()

const CommentsInput: React.FC<CommentsInputProps> = ({ taskSKs, expanded, checklistItems, context }) => {
  const { PK, SK } = useParams()
  const orgId = PK?.split?.('ORG_')?.[1]
  const location = useLocation()
  const theme = useTheme()
  const queryClient = useQueryClient()
  const { editorInstance, setEditorInstance } = useEditorContext()
  const { syncTableCacheAfterDeletion } = useSyncQueryCache()
  const { deals: allDeals, organizations, commentsTable } = useEntitiesFromSearch()
  // const { data: commentsQuery, isLoading: isCommentsQueryLoading } = useQueryComments(PK)

  const currentOrganization = organizations?.find(o => o?.PK === PK)
  const organization = currentOrganization as Organization
  const currentDeal = allDeals?.find((d: OrganizationTableItem) => d?.SK === SK) ?? null

  const [selectedTask, setSelectedTask] = useState<SelectedTask>(null)
  const [searchParams, setSearchParams] = useSearchParams()

  const { data: allUsers } = useUserItems()
  const { activeUser } = useActiveUser()
  const { users: usersById } = useUsersById(activeUser?.user?.id ? [activeUser.user.id] : [])
  const currentUser = usersById?.[0]

  const { handlePostComment } = useHandlePostComment()
  const { notify } = useUniversalNotify()
  const { mutate: sendSlackNotification } = useSlackNotification()
  const { showSnackbar } = useSnackbarContext()
  const { mutateAsync: uploadAttachments } = useUploadNoteAttachments()
  const { handleDeleteAttachment } = useDeleteAttachment()
  const { mutate: deleteCommentAndAttachments } = useDeleteCommentAndAttachments()

  // editor state
  const [newNoteContent, setNewNoteContent] = useState('')
  const [selectedUsers, setSelectedUsers] = useState<any[]>([])
  const [selectedFiles, setSelectedFiles] = useState<any[]>([])
  const [isCreatingNote, setIsCreatingNote] = useState(false)
  const [isDeleting, setIsDeleting] = useState<boolean>(false)

  // preview overlay
  const [isFrameOpen, setIsFrameOpen] = useState(false)
  const [selectedEmbedUrl, setSelectedEmbedUrl] = useState<string | null>(null)

  // ---------- COMMENT SOURCES (single responsibility each) ----------
  // const all = useMemo(() => {
  //   if (!commentsQuery || isCommentsQueryLoading) return []
  //   return commentsQuery ?? []
  // }, [commentsQuery, isCommentsQueryLoading])
  const all = useMemo(() => {
    if (!commentsTable) return []
    return commentsTable ?? []
  }, [commentsTable])
  // Task thread (when a task is selected)
  const taskThreadComments = useMemo(() => {
    if (!selectedTask) return []
    return all.filter(c => c?.PK === selectedTask?.SK).sort(byNewest)
  }, [all, selectedTask])
  const NCO_COMMENTS = useMemo(() => {
    if (!PK || !SK) return []
    return all.filter(c => c?.PK === PK && c?.context === 'nco').sort(byNewest)
  }, [all, PK, SK])
  const MTB_COMMENTS = useMemo(() => {
    if (!PK) return []
    return all.filter(c => c?.PK === PK && c?.context === 'mtb').sort(byNewest)
  }, [all, PK, SK])
  const INTEGRATION_COMMENTS = useMemo(() => {
    if (!PK) return []
    return all.filter(c => c?.PK === PK && c?.context === 'integration').sort(byNewest)
  }, [all, PK, SK])
  // Deal thread (org PK + dealSK)
  const dealThreadComments = useMemo(() => {
    if (!PK || !SK) return []
    return all.filter(c => c?.PK === PK && c?.dealSK === SK).sort(byNewest)
  }, [all, PK, SK])
  // Org thread (org PK only, no dealSK)
  const orgThreadComments = useMemo(() => {
    if (!PK) return []
    return all.filter(c => c?.PK === PK && !c?.dealSK).sort(byNewest)
  }, [all, PK])

  // Accounting-only (subset of deal thread)
  const accountingOnlyComments = useMemo(() => {
    return dealThreadComments.filter(c => ['deals', 'accounting']?.includes(c?.context))
  }, [dealThreadComments])
  // comment counts for TaskSelector badges
  const taskCommentData = useMemo(() => {
    return all.filter((c: any) => taskSKs?.includes(c?.PK))
  }, [all, taskSKs])

  const taskCommentCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const sk of taskSKs) {
      counts[sk] = taskCommentData.filter(comment => comment?.PK === sk).length
    }
    return counts
  }, [taskCommentData, taskSKs])

  // Users involved (owners) for current visible context
  const userIds = useMemo(() => {
    const src = selectedTask
      ? taskThreadComments
      : context === 'deals'
      ? dealThreadComments
      : context === 'accounting'
      ? accountingOnlyComments
      : orgThreadComments

    return src?.flatMap(n => n?.owner || []) || []
  }, [selectedTask, context, taskThreadComments, dealThreadComments, accountingOnlyComments, orgThreadComments])

  const users = useMemo(() => {
    return allUsers?.filter((u: User) => userIds?.includes(u?.user_uuid)) ?? []
  }, [allUsers, userIds]) as User[]

  // ---------- URL param ↔ selected task ----------
  useEffect(() => {
    const taskParam = searchParams.get('task')
    if (taskParam && checklistItems?.length > 0) {
      const task = (checklistItems as any[]).find(item => item.SK === taskParam)
      if (task) setSelectedTask(task)
    }
  }, [searchParams, checklistItems])

  const handleTaskSelect = (task: any | null) => {
    if (task) {
      setSelectedTask(task)
      setSearchParams({ task: task.SK })
    } else {
      setSelectedTask(null)
      setSearchParams({})
    }
  }

  // ---------- Create (post) comment ----------
  const handleCreateAndNotify = async () => {
    if (!newNoteContent || /^<p>(<br>)?<\/p>$/.test(newNoteContent)) return

    setIsCreatingNote(true)
    try {
      let uploaded: Attachments[] = []
      let contentsHtml = newNoteContent

      if (selectedFiles.length > 0) {
        const rawFiles = selectedFiles.map(f => f.file)
        const fileOrgId = selectedTask?.orgId || orgId
        uploaded = await uploadAttachments({
          files: rawFiles,
          orgId: fileOrgId,
        })

        // Map preview blob → final S3 URL in the HTML
        const blobToFinal = new Map<string, string>()
        selectedFiles.forEach((sf, i) => {
          const blob = sf.previewUrl || sf.url
          const final = uploaded[i]?.url
          if (blob && final) blobToFinal.set(blob, final)
        })
        blobToFinal.forEach((finalUrl, blobUrl) => {
          contentsHtml = contentsHtml.split(blobUrl).join(finalUrl)
        })
      }

      // canonical context to store
      const canonicalContext = selectedTask
        ? 'task'
        : context === 'accounting'
        ? 'accounting'
        : context === 'deals'
        ? 'deals'
        : context === 'mtb'
        ? 'mtb'
        : context === 'nco'
        ? 'nco'
        : context === 'projects'
        ? 'projects'
        : context ?? 'organization'

      const currentLocation = location.pathname.split('/')[1] ?? null

      const commentData = {
        // PK selection:
        // - task thread → PK = task SK
        // - otherwise org thread → PK = ORG_...
        PK: selectedTask?.SK || `ORG_${orgId}`,
        orgId: selectedTask?.orgId || orgId,
        title: selectedTask?.name || organization?.name || '',
        attachments: uploaded,
        owner: activeUser?.user ? [activeUser.user.id] : [],
        taggedUsers: selectedUsers,
        tagIds: selectedUsers.map(u => u.user_uuid || u.id),
        createdAt: new Date().toISOString(),
        context: canonicalContext,
        contents: contentsHtml,
        identifier:
          selectedTask && selectedTask?.SK?.startsWith('OPTIMIZATION')
            ? selectedTask?.name
            : selectedTask
            ? 'Task'
            : canonicalContext,
        isDigital: context === 'nco',
        isCreative: context === 'creative',
        isWebsite: context === 'website',
        name: activeUser?.user?.fullName || 'Unknown User',

        // Deal-aware contexts
        ...((context === 'deals' || context === 'accounting') && SK ? { dealSK: SK } : {}),
        ...((context === 'deals' || context === 'accounting') && currentDeal ? { dealName: currentDeal?.name } : {}),
      }
      await handlePostComment(commentData)

      const notification = {
        users: selectedUsers,
        type: 'mention',
        severity: 'success',
        title: 'Mention',
        origin: currentLocation,
        org_uuid: selectedTask?.orgId ?? orgId ?? null,
        message: `You've been tagged in a comment`,
        board: 'teamtasks',
        boardItemName: selectedTask?.name || organization?.name,
        boardId: selectedTask?.SK || organization?.org_uuid,
        slackHeaderMessage: 'You were tagged in a comment!',
        slackMessageHtml: newNoteContent,
        slackDirectLink: location.pathname + location.search,
        sendSlack: true,
        sendApp: true,
      }
      // Notify tagged users
      await notify({ data: notification, dry_run: false })

      // clear editor state
      setNewNoteContent('')
      setSelectedUsers([])
      setSelectedFiles([])
    } catch (error) {
      console.error('❌ Failed to create comment or notify:', error)
      showSnackbar('Failed to create comment or notify', 'error')
    } finally {
      setIsCreatingNote(false)
    }
  }

  // ---------- Delete comment ----------
  const handleDeleteNote = async (note: any) => {
    if (!note?.PK || !note?.SK) return
    try {
      await deleteCommentAndAttachments({
        note,
        queryKeysToInvalidate: [],
      })
      // optimistic cleanup in the table cache
      syncTableCacheAfterDeletion(queryClient, note, ['commentsTable'])
    } catch (e) {
      showSnackbar('Failed to delete comment', 'error')
    }
  }

  // ---------- Delete attachment ----------
  const onDeleteAttachment = async (noteId: string, attachmentIndex: number) => {
    setIsDeleting(true)
    await handleDeleteAttachment(all, noteId, attachmentIndex)
    setIsDeleting(false)
  }

  // ---------- Frame overlay ----------
  const openFrame = (url: string) => {
    setSelectedEmbedUrl(url)
    setIsFrameOpen(true)
  }
  const closeFrame = () => {
    setIsFrameOpen(false)
    setSelectedEmbedUrl(null)
  }

  // ---------- Pick exactly ONE list to render ----------
  const visibleComments = useMemo(() => {
    if (selectedTask) return taskThreadComments
    switch (context) {
      case 'deals':
        return dealThreadComments
      case 'accounting':
        // choose one: accounting-only or the entire deal thread
        return accountingOnlyComments // or: return dealThreadComments
      case 'nco':
        return NCO_COMMENTS
      case 'mtb':
        return MTB_COMMENTS
      case 'integration':
        return INTEGRATION_COMMENTS
      default:
        return orgThreadComments
    }
  }, [selectedTask, context, taskThreadComments, dealThreadComments, accountingOnlyComments, orgThreadComments])
  // ---------- Render ----------
  return (
    <Grid container pt={1} sx={{ display: 'flex' }}>
      <Grid item xs={12} sx={{ px: 2, py: 1 }}>
        <TextEditor
          organization={organization}
          deal={currentDeal}
          onUpload={e => {
            const files = Array.from(e.target.files || []).map(file => ({
              id: file.name + Date.now(),
              name: file.name,
              mimeType: file.type,
              previewUrl: URL.createObjectURL(file),
              file,
            }))
            setSelectedFiles(prev => [...prev, ...files])
          }}
          onRemoveFile={i => setSelectedFiles(prev => prev.filter((_, index) => index !== i))}
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          selectedTask={selectedTask}
          onContentChange={setNewNoteContent}
          onSave={handleCreateAndNotify}
          newNoteContent={newNoteContent}
          onMentionsChange={setSelectedUsers}
          height={!expanded ? '5vh' : '35vh'}
        />
      </Grid>

      <Grid
        item
        xs={12}
        sx={{
          maxHeight: !expanded ? '80vh' : '65vh',
          overflowY: 'hidden',
          p: 1,
        }}
      >
        {/* Show task selector only when this is not deals/org/integration/accounting */}
        {!['deals', 'organization', 'integration', 'accounting']?.includes(context) ? (
          <Box py={1}>
            <TaskSelector
              checklistItems={checklistItems}
              onTaskSelected={handleTaskSelect}
              selectedTask={selectedTask}
              taskComments={taskCommentData}
              commentCounts={taskCommentCounts}
            />
          </Box>
        ) : null}

        {visibleComments?.length > 0 ? (
          <CommentList
            notes={visibleComments}
            users={users}
            openFrame={openFrame}
            onDeleteNote={handleDeleteNote}
            expanded={expanded}
            context={context}
            onDeleteAttachment={onDeleteAttachment}
          />
        ) : (
          <PlaceholderComment expanded={expanded} />
        )}
      </Grid>

      {isFrameOpen && selectedEmbedUrl && (
        <Box
          onClick={closeFrame}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0,0,0,0.8)',
            zIndex: 1300,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Box sx={{ width: '80%', height: '80%', position: 'relative' }}>
            <iframe src={selectedEmbedUrl} style={{ width: '100%', height: '100%' }} title="File Preview" />
          </Box>
        </Box>
      )}
    </Grid>
  )
}

export default CommentsInput
